// stocai_frontend/src/components/HomePage/components/widgets/Module7/VisualizeFuture.jsx

import React, { useState, useEffect, useCallback, useRef } from "react";
import WidgetContainer from "../WidgetContainer.jsx";
import pandaNarrativeImg from "../../../../../assets/insight_panda.png";
import { progressService } from "../../../../../services/progressService";
import FuturePromotionStep from "./Steps/FuturePromotionStep.jsx";
import ThinkWhatMadeThisPossibleStep from "./Steps/ThinkWhatMadeThisPossibleStep.jsx";
import WhoYouBecameStep from "./Steps/WhoYouBecameStep";
import CelebrationFutureSelf from "../../Celebration/CelebrationFutureSelf";
import ComparisonScreen from "./Steps/ComparisonScreen.jsx";
import FutureSequence from "./Steps/FutureSequence.jsx";

const DEFAULT_FUTURE_IDENTITY_SUBSECTION_ID =
  "a6f1a312-7580-4988-8cc2-64d342be7ab3";
const CURRENT_IDENTITY_SUBSECTION_ID =
  "f90552a1-12fd-4f0b-b4f5-17e258b84b97";
const FUTURE_VALUES_SUBSECTION_ID = "f135631c-ac59-4ac1-8d68-7f522948d56f";
// Module 6 Building Narrative subsection - for loading selectedCard data
const BUILDING_NARRATIVE_SUBSECTION_ID = "de0d5ab2-0fa7-48cd-88c2-d6a56bb64853";


const mapTimelineToMonths = (timeline) => {
  if (timeline === undefined || timeline === null) return 12;
  if (typeof timeline === "number") return timeline;
  const numeric = parseInt(String(timeline).match(/\d+/)?.[0] || "", 10);
  if (!Number.isNaN(numeric)) return numeric;
  const t = String(timeline).toLowerCase();
  if (t.includes("less than 6")) return 6;
  if (t.includes("6-12") || t.includes("6 to 12")) return 12;
  if (t.includes("12-24") || t.includes("12 to 24") || t.includes("1-2 years"))
    return 24;
  if (t.includes("more than 24") || t.includes("2+ years") || t.includes("more than 2"))
    return 36;
  return 12;
};

const VisualizeFuture = ({
  userId,
  subsectionId,
  futureIdentitySubsectionId = DEFAULT_FUTURE_IDENTITY_SUBSECTION_ID,
  onComplete,
  onPhaseChange,
}) => {
  const [futureTitle, setFutureTitle] = useState("");
  const [monthsAhead, setMonthsAhead] = useState(12);
  const [futureDescription, setFutureDescription] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const [skipLevelManagerNotice, setSkipLevelManagerNotice] = useState("");
  const [directManagerNotice, setDirectManagerNotice] = useState("");
  const [thirdPerspectiveNotice, setThirdPerspectiveNotice] = useState("");
  const [beliefs, setBeliefs] = useState([{ id: 1, text: "" }, { id: 2, text: "" }]);
  const [behaviours, setBehaviours] = useState([{ id: 1, text: "" }, { id: 2, text: "" }]);
  const [risks, setRisks] = useState([{ id: 1, text: "" }, { id: 2, text: "" }]);
  const [coreValues, setCoreValues] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);

  const existingPayloadRef = useRef(null);
  const autosaveTimerRef = useRef(null);
  const isCompletedRef = useRef(false);

  // future identity data for FutureSequence final slide
  const [futureSeqData, setFutureSeqData] = useState(null);

  const loadFutureIdentity = useCallback(async () => {
    if (!userId || !futureIdentitySubsectionId) return;
    try {
      const record = await progressService.getUserProgress(
        userId,
        futureIdentitySubsectionId
      );
      if (!record?.data) return;
      const payload = record.data.payload || record.data || {};
      if (payload.designation) setFutureTitle(payload.designation);
      else if (payload.title) setFutureTitle(payload.title);
      if (payload.timeline !== undefined && payload.timeline !== null) {
        setMonthsAhead(mapTimelineToMonths(payload.timeline));
      } else if (payload.monthsAhead) {
        setMonthsAhead(Number(payload.monthsAhead));
      }
    } catch (err) {
      console.error("Failed to load Future Identity data:", err);
    }
  }, [userId, futureIdentitySubsectionId]);

  useEffect(() => {
    async function loadFutureValues() {
      if (!userId || !FUTURE_VALUES_SUBSECTION_ID) return;
      try {
        const rec = await progressService.getUserProgress(
          userId,
          FUTURE_VALUES_SUBSECTION_ID
        );
        const payload = rec?.data?.payload || rec?.data || {};
        if (Array.isArray(payload.top3Values)) {
          setCoreValues(payload.top3Values.filter(Boolean));
        }
      } catch (err) {
        console.error("Failed to load top3 values for FutureSequence:", err);
      }
    }
  
    loadFutureValues();
  }, [userId]);

  // Load narrative data from Module 6 for NarrativeCompletionCard
  useEffect(() => {
    async function loadNarrativeData() {
      if (!userId || !BUILDING_NARRATIVE_SUBSECTION_ID) return;
      try {
        const rec = await progressService.getUserProgress(
          userId,
          BUILDING_NARRATIVE_SUBSECTION_ID
        );
        const payload = rec?.data?.payload || rec?.data || {};
        if (payload.selectedCard) {
          setSelectedCard(payload.selectedCard);
        }
      } catch (err) {
        console.error("Failed to load narrative data for FutureSequence:", err);
      }
    }

    loadNarrativeData();
  }, [userId]);
  

  const loadOwnProgress = useCallback(async () => {
    if (!userId || !subsectionId) return;
    try {
      const record = await progressService.getUserProgress(userId, subsectionId);
      if (!record?.data) return;
      const payload = record.data.payload || record.data || {};
      existingPayloadRef.current = { ...(payload || {}) };

      if (typeof payload.futureDescription === "string")
        setFutureDescription(payload.futureDescription);
      if (payload.futureTitle) setFutureTitle(payload.futureTitle);
      if (typeof payload.monthsAhead === "number")
        setMonthsAhead(payload.monthsAhead);

      if (typeof payload.skipLevelManagerNotice === "string")
        setSkipLevelManagerNotice(payload.skipLevelManagerNotice);
      if (typeof payload.directManagerNotice === "string")
        setDirectManagerNotice(payload.directManagerNotice);
      if (typeof payload.thirdPerspectiveNotice === "string")
        setThirdPerspectiveNotice(payload.thirdPerspectiveNotice);

      if (Array.isArray(payload.beliefs)) {
        setBeliefs(payload.beliefs.map((t, i) => ({ id: i + 1, text: t || "" })));
      }
      if (Array.isArray(payload.behaviours)) {
        setBehaviours(payload.behaviours.map((t, i) => ({ id: i + 1, text: t || "" })));
      }
      if (Array.isArray(payload.risks)) {
        setRisks(payload.risks.map((t, i) => ({ id: i + 1, text: t || "" })));
      }
    } catch (err) {
      console.error("Failed to load Visualise Future progress:", err);
    }
  }, [userId, subsectionId]);

  // load data for FutureSequence final FutureIdentityCard
  useEffect(() => {
    async function loadFutureSeqData() {
      if (!userId) return;
      try {
        const [futureIdentityRec, currentIdentityRec] = await Promise.all([
          progressService.getUserProgress(userId, DEFAULT_FUTURE_IDENTITY_SUBSECTION_ID),
          progressService.getUserProgress(userId, CURRENT_IDENTITY_SUBSECTION_ID),
        ]);

        const futurePayload =
          futureIdentityRec?.data?.payload || futureIdentityRec?.data || {};
        const currentPayload =
          currentIdentityRec?.data?.payload || currentIdentityRec?.data || {};

        setFutureSeqData({
          userName: currentPayload.userName || "User",
          profileImageUrl: currentPayload.profileImage || "",
          designation:
            futurePayload.designation ||
            futurePayload.futureTitle ||
            currentPayload.currentTitle ||
            "Your Role",
          salary: futurePayload.salary || currentPayload.salary || "",
          timeline: futurePayload.timeline || "",
          location:
            futurePayload.location ||
            currentPayload.location ||
            "",
          knownFor: Array.isArray(futurePayload.knownFor)
            ? futurePayload.knownFor
            : [],
          futureVoiceNote:
            currentPayload.futureVoiceNote ||
            futurePayload.futureVoiceNote ||
            "",
        });
      } catch (err) {
        console.error("Failed to load future sequence data:", err);
      }
    }

    loadFutureSeqData();
  }, [userId]);

  useEffect(() => {
    loadOwnProgress();
    loadFutureIdentity();
  }, [loadOwnProgress, loadFutureIdentity]);

  const buildMergedPayload = useCallback(
    (overridesPayload = {}) => {
      const base =
        existingPayloadRef.current &&
        typeof existingPayloadRef.current === "object"
          ? { ...existingPayloadRef.current }
          : {};
      const mergedPayload = {
        ...base,
        futureDescription,
        futureTitle,
        monthsAhead,
        skipLevelManagerNotice,
        directManagerNotice,
        thirdPerspectiveNotice,
        beliefs: beliefs.map((b) =>
          typeof b === "string" ? b : b.text || ""
        ),
        behaviours: behaviours.map((b) =>
          typeof b === "string" ? b : b.text || ""
        ),
        risks: risks.map((r) => (typeof r === "string" ? r : r.text || "")),
        ...overridesPayload,
      };
      return { type: "widget", payload: mergedPayload };
    },
    [
      futureDescription,
      futureTitle,
      monthsAhead,
      skipLevelManagerNotice,
      directManagerNotice,
      thirdPerspectiveNotice,
      beliefs,
      behaviours,
      risks,
    ]
  );

  const ensureCompleted = useCallback(async () => {
    if (!userId || !subsectionId) return;
    if (isCompletedRef.current) return;
    try {
      const dataToSave = buildMergedPayload();
      await progressService.saveMergedProgress(
        userId,
        subsectionId,
        dataToSave,
        "completed"
      );
      isCompletedRef.current = true;
    } catch (err) {
      console.error("Error saving Visualise Future completion:", err);
    }
  }, [userId, subsectionId, buildMergedPayload]);

  const saveProgress = useCallback(
    async (status = "in_progress", overridesPayload = {}) => {
      if (!userId || !subsectionId) return;
      if (status === "in_progress" && isCompletedRef.current) return;
      try {
        if (status === "in_progress") setIsSaving(true);
        const dataToSave = buildMergedPayload(overridesPayload);
        const result = await progressService.saveMergedProgress(
          userId,
          subsectionId,
          dataToSave,
          status
        );
        existingPayloadRef.current = {
          ...(existingPayloadRef.current || {}),
          ...(dataToSave.payload || {}),
        };
        setIsSaving(false);
        return result;
      } catch (err) {
        console.error("Error saving Visualise Future progress:", err);
        setIsSaving(false);
        throw err;
      }
    },
    [userId, subsectionId, buildMergedPayload]
  );

  useEffect(() => {
    if (!userId || !subsectionId) return;

    const hasContent =
      (futureDescription && futureDescription.trim()) ||
      (skipLevelManagerNotice && skipLevelManagerNotice.trim()) ||
      beliefs.some((b) => b.text && b.text.trim()) ||
      behaviours.some((b) => b.text && b.text.trim()) ||
      risks.some((r) => r.text && r.text.trim());
    if (!hasContent) return;

    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => {
      saveProgress("in_progress").catch(() => {});
    }, 1200);

    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
  }, [
    futureDescription,
    skipLevelManagerNotice,
    directManagerNotice,
    thirdPerspectiveNotice,
    beliefs,
    behaviours,
    risks,
    saveProgress,
    userId,
    subsectionId,
  ]);

  const handleFinalComplete = async (overrides = {}) => {
    if (overrides && Object.keys(overrides).length > 0) {
      await saveProgress("completed", overrides);
      isCompletedRef.current = true;
    } else {
      await ensureCompleted();
    }
    if (onComplete) onComplete();
  };

  return (
    <WidgetContainer
      userId={userId}
      subsectionId={subsectionId}
      onComplete={handleFinalComplete}
      onPhaseChange={onPhaseChange}
      intro={{
        image: pandaNarrativeImg,
        imageClassName: "w-[302.8968px] h-[315.0804px] object-contain mt-20 mx-auto",
        title: "Visualise the Future",
        description:
          "You know who you must become. Let’s ensure that your stakeholders see and believe in your evolved avatar.",
        buttonLabel: "Get Started",
      }}
      steps={[
        {
          id: "future_promotion",
          component: FuturePromotionStep,
          props: {
            monthsAhead,
            futureTitle,
            textareaValue: futureDescription,
            setTextareaValue: setFutureDescription,
            isSaving,
            onNextStep: () => {}, // handled by WidgetContainer
          },
        },
        {
          id: "future_reflection",
          component: ThinkWhatMadeThisPossibleStep,
          props: {
            userId,
            subsectionId,
            skipLevelManagerNotice,
            setSkipLevelManagerNotice,
            directManagerNotice,
            setDirectManagerNotice,
            thirdPerspectiveNotice,
            setThirdPerspectiveNotice,
            onNextStep: () => {},
          },
        },
        {
          id: "who_you_became",
          component: WhoYouBecameStep,
          props: {
            userId,
            subsectionId,
            beliefs,
            setBeliefs,
            behaviours,
            setBehaviours,
            risks,
            setRisks,
            onNextStep: () => {},
          },
        },
        {
          id: "celebration_future_self",
          component: CelebrationFutureSelf,
          props: {
            futureIdentitySubsectionId,
            currentIdentitySubsectionId: CURRENT_IDENTITY_SUBSECTION_ID,
            onNextStep: () => {},
          },
          fullScreen: true,
        },
        {
          id: "comparison_future_self",
          component: ComparisonScreen,
          props: {
            onNextStep: () => {}, // will advance to future_sequence
          },
          fullScreen: true,
        },
        {
          id: "future_sequence",
          component: FutureSequence,
          props: {
            userId,
            selectedCard: selectedCard,
            futureData: futureSeqData || {},
            coreValues,                                  
            behaviours: behaviours.map((b) => b.text || ""),
            beliefs: beliefs.map((b) => b.text || ""),
            risks: risks.map((r) => r.text || ""),
            onNextStep: handleFinalComplete,
          },
          fullScreen: true,
        },        
      ]}
    />
  );
};

export default VisualizeFuture;
